import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize, resolveField, ShSeverity } from '../models';
import { buildOptionItems, ShOptionAccessor } from '../select/option-items';

/** What the template needs per option; built once per `options` value. */
interface SegmentedItem {
  /** The option's position: the key PrimeNG sees, so a `null` value works. */
  key: number;
  label: string;
  value: unknown;
  icon: string | undefined;
  /** Central class `sh-sev-<severity>` for the selected look. */
  severityClass: string;
}

/**
 * A row of connected buttons of which exactly one is selected (PrimeNG's
 * `p-selectbutton`). The value is the option's `optionValue`, and any value
 * works, `null` included (e.g. "inherit" next to true / false).
 *
 *   <sh-segmented
 *     [options]="states"
 *     optionLabel="label"
 *     optionValue="value"
 *     optionIcon="icon"
 *     optionSeverity="severity"
 *     [iconOnly]="true"
 *     size="sm"
 *     [(value)]="state" />
 *
 * `optionLabel`, `optionValue`, `optionIcon` and `optionSeverity` are a
 * (dotted) field name or a function. Labels are shown as they are (translate
 * them before passing them in); with `iconOnly` they are only the tooltip and
 * the accessible name. `optionSeverity` colours the option while it is
 * selected (`secondary`, the default, is the neutral grey).
 *
 * Clicking the selected option keeps it selected; `allowEmpty` lets it clear
 * the value to `null` instead. The control looks come from the preset
 * (`selectbutton`, `togglebutton`) and the `.sh-segmented` classes in
 * `@seahorse/theme`.
 */
@Component({
  selector: 'sh-segmented',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectButtonModule,
    PrimeTemplate,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShSegmentedComponent),
      multi: true,
    },
  ],
  host: {
    class: 'sh-control sh-segmented',
    '[class.sh-invalid]': 'isInvalid()',
  },
  template: `
    <p-selectbutton
      [options]="items()"
      optionLabel="label"
      optionValue="key"
      [ngModel]="selectedKey()"
      (ngModelChange)="onKey($event)"
      [ngModelOptions]="{ standalone: true }"
      [allowEmpty]="allowEmpty()"
      [size]="pSize()"
      [disabled]="isDisabled() || isReadonly()"
      [attr.aria-label]="ariaLabel() | translate"
    >
      <ng-template pTemplate="item" let-option>
        <span
          class="sh-segmented-item"
          [ngClass]="option.severityClass"
          [attr.title]="iconOnly() ? option.label : null"
          [attr.aria-label]="iconOnly() ? option.label : null"
          [attr.role]="iconOnly() ? 'img' : null"
        >
          <i *ngIf="option.icon" [class]="option.icon" aria-hidden="true"></i>
          <span *ngIf="!iconOnly()">{{ option.label }}</span>
        </span>
      </ng-template>
    </p-selectbutton>
  `,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ShSegmentedComponent<T = any> extends ShFormControl<unknown> {
  readonly options = input<readonly T[] | null | undefined>();
  readonly optionLabel = input<ShOptionAccessor<T, string>>();
  readonly optionValue = input<ShOptionAccessor<T, unknown>>();
  /** Font Awesome classes per option. */
  readonly optionIcon = input<ShOptionAccessor<T, string | undefined>>();
  readonly optionSeverity =
    input<ShOptionAccessor<T, ShSeverity | undefined>>();
  /** Only the icons are shown; the labels become tooltip and accessible name. */
  readonly iconOnly = input(false, { transform: booleanAttribute });
  /** Clicking the selected option clears the value to `null`. */
  readonly allowEmpty = input(false, { transform: booleanAttribute });

  // A computed, not a getter: PrimeNG must get the same array until the
  // options (or how they are read) really change.
  protected readonly items = computed<SegmentedItem[]>(() => {
    const options = this.options();
    const icon = this.optionIcon();
    const severity = this.optionSeverity();
    return buildOptionItems(
      options,
      this.optionLabel(),
      this.optionValue()
    ).map((item, key) => {
      const iconValue =
        typeof icon === 'function'
          ? icon(item.raw)
          : icon
          ? resolveField(item.raw, icon)
          : undefined;
      const severityValue =
        typeof severity === 'function'
          ? severity(item.raw)
          : severity
          ? resolveField(item.raw, severity)
          : undefined;
      return {
        key,
        label: item.label,
        value: item.value ?? null,
        icon: iconValue ? String(iconValue) : undefined,
        severityClass: `sh-sev-${severityValue ?? 'secondary'}`,
      };
    });
  });

  protected readonly selectedKey = computed<number | null>(() => {
    const value = this.value() ?? null;
    const found = this.items().find((item) => item.value === value);
    return found ? found.key : null;
  });

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );

  protected onKey(key: number | null | undefined): void {
    const item = key === null || key === undefined ? null : this.items()[key];
    this.commit(item ? item.value : null);
  }
}
